import { useEffect, useState } from "react";
import { Users, Radio, ThumbsUp, MessageCircle, Download, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Stat, Btn, Loading } from "./AdminUI";
import { DailyVisitorReport } from "./DailyVisitorReport";

const Delta = ({ now, prev, label }) => {
  const diff = (now || 0) - (prev || 0);
  const up = diff >= 0;
  return (
    <div className="rounded-xl border border-[#273042] bg-[#0A0C10] p-4">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-2xl font-extrabold text-white mt-1">{(now || 0).toLocaleString()}</div>
      <div className={`text-xs font-bold mt-1 flex items-center gap-1 ${up ? "text-emerald-400" : "text-red-400"}`}>
        {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {up ? "+" : ""}{diff.toLocaleString()} vs previous
      </div>
    </div>
  );
};

export default function AdminEngagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => endpoints.adminEngagementReport().then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const exportCsv = async () => {
    try { const { data: blob } = await endpoints.adminEngagementCsv();
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "engagement-report.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  if (loading || !data) return <Loading />;
  const { totals, daily, weekly, comparison } = data;

  return (
    <div>
      <PageHeader title="Engagement Reports" desc="How active your members are — updated live.">
        <Btn onClick={exportCsv} variant="ghost" data-testid="engagement-export"><Download className="w-4 h-4" /> Export CSV</Btn>
      </PageHeader>

      <DailyVisitorReport />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat label="Total members" value={totals.totalUsers.toLocaleString()} icon={Users} color="#2563EB" />
        <Stat label="Live sessions" value={totals.totalLiveSessions.toLocaleString()} icon={Radio} color="#E11D2A" />
        <Stat label="Live joiners" value={totals.totalJoiners.toLocaleString()} icon={Users} color="#22C55E" />
        <Stat label="Total likes" value={totals.totalLikes.toLocaleString()} icon={ThumbsUp} color="#F5B301" />
        <Stat label="Total comments" value={totals.totalComments.toLocaleString()} icon={MessageCircle} color="#A855F7" />
      </div>

      <Card className="p-5 mb-6">
        <h3 className="text-white font-bold mb-4">Today vs Yesterday · This week vs Last week</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Delta label="Joiners today" now={comparison.today.joiners} prev={comparison.yesterday.joiners} />
          <Delta label="Likes today" now={comparison.today.likes} prev={comparison.yesterday.likes} />
          <Delta label="Comments today" now={comparison.today.comments} prev={comparison.yesterday.comments} />
          <Delta label="Joiners this week" now={comparison.thisWeek.joiners} prev={comparison.lastWeek.joiners} />
        </div>
      </Card>

      <Card className="p-5 mb-6">
        <h3 className="text-white font-bold mb-4">Daily activity · last 14 days</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={daily.map((d) => ({ ...d, date: d.date.slice(5) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#273042" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} />
            <Tooltip contentStyle={{ background: "#121620", border: "1px solid #273042", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(225,29,42,0.08)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="joiners" name="Joiners" fill="#22C55E" radius={[3, 3, 0, 0]} />
            <Bar dataKey="likes" name="Likes" fill="#F5B301" radius={[3, 3, 0, 0]} />
            <Bar dataKey="comments" name="Comments" fill="#A855F7" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-[#273042] text-white font-bold text-sm">Weekly summary · last 8 weeks</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-slate-400 text-xs uppercase tracking-wider">
              {["Week", "New users", "Live sessions", "Joiners", "Likes", "Comments"].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {weekly.slice().reverse().map((w) => (
                <tr key={w.weekStart} className="border-t border-[#273042]" data-testid={`week-row-${w.weekStart}`}>
                  <td className="px-4 py-2.5 text-slate-300">{w.weekStart.slice(5)} – {w.weekEnd.slice(5)}</td>
                  <td className="px-4 py-2.5 text-white font-semibold">{w.newUsers}</td>
                  <td className="px-4 py-2.5 text-slate-300">{w.liveSessions}</td>
                  <td className="px-4 py-2.5 text-emerald-400">{w.joiners}</td>
                  <td className="px-4 py-2.5 text-[#F5B301]">{w.likes}</td>
                  <td className="px-4 py-2.5 text-[#C4B5FD]">{w.comments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
